// components/GeofenceManager.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Slider,
    Grid
} from "@mui/material";
import {
    LocationOn as LocationOnIcon,
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

const GeofenceManager = ({ orgId }) => {
    const [geofences, setGeofences] = useState([]);
    const [newGeofence, setNewGeofence] = useState({
        name: "",
        latitude: "",
        longitude: "",
        radius: 100, // Default radius in meters
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchGeofences();
    }, []);

    const fetchGeofences = async () => {
        setLoading(true);
        try {
            const geofencesRef = collection(db, "organizations", orgId, "geofences");
            const snapshot = await getDocs(geofencesRef);
            const geofenceData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setGeofences(geofenceData);
        } catch (err) {
            console.error("Error fetching geofences:", err.message);
            setError("Failed to load geofences. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewGeofence({
            ...newGeofence,
            [name]: name === "radius" ? Number(value) : value
        });
    };

    const handleSliderChange = (_, newValue) => {
        setNewGeofence({
            ...newGeofence,
            radius: newValue
        });
    };

    const validateGeofence = () => {
        // Simple validation
        if (!newGeofence.name) {
            setError("Geofence name is required");
            return false;
        }

        const lat = parseFloat(newGeofence.latitude);
        const lng = parseFloat(newGeofence.longitude);

        if (isNaN(lat) || lat < -90 || lat > 90) {
            setError("Invalid latitude. Must be between -90 and 90.");
            return false;
        }

        if (isNaN(lng) || lng < -180 || lng > 180) {
            setError("Invalid longitude. Must be between -180 and 180.");
            return false;
        }

        return true;
    };

    const addGeofence = async () => {
        if (!validateGeofence()) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const geofenceData = {
                name: newGeofence.name,
                latitude: parseFloat(newGeofence.latitude),
                longitude: parseFloat(newGeofence.longitude),
                radius: newGeofence.radius,
                createdAt: serverTimestamp(),
                active: true
            };

            await addDoc(collection(db, "organizations", orgId, "geofences"), geofenceData);

            setSuccess("Geofence added successfully!");
            setNewGeofence({
                name: "",
                latitude: "",
                longitude: "",
                radius: 100
            });

            // Refresh the list
            fetchGeofences();
        } catch (err) {
            console.error("Error adding geofence:", err.message);
            setError("Failed to add geofence. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const deleteGeofence = async (id) => {
        setLoading(true);
        try {
            await deleteDoc(doc(db, "organizations", orgId, "geofences", id));
            setSuccess("Geofence deleted successfully!");
            fetchGeofences();
        } catch (err) {
            console.error("Error deleting geofence:", err.message);
            setError("Failed to delete geofence. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Optional: Function to get current location
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setNewGeofence({
                        ...newGeofence,
                        latitude: position.coords.latitude.toString(),
                        longitude: position.coords.longitude.toString()
                    });
                },
                (error) => {
                    console.error("Error getting location:", error);
                    setError("Unable to get current location. Please enter coordinates manually.");
                }
            );
        } else {
            setError("Geolocation is not supported by this browser.");
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Geofence Manager
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
                Set up geographic boundaries where employees can clock in and out.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Add New Geofence
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Geofence Name"
                            name="name"
                            value={newGeofence.name}
                            onChange={handleInputChange}
                            disabled={loading}
                            margin="normal"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Latitude"
                            name="latitude"
                            value={newGeofence.latitude}
                            onChange={handleInputChange}
                            disabled={loading}
                            margin="normal"
                            type="number"
                            inputProps={{ step: "0.000001" }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Longitude"
                            name="longitude"
                            value={newGeofence.longitude}
                            onChange={handleInputChange}
                            disabled={loading}
                            margin="normal"
                            type="number"
                            inputProps={{ step: "0.000001" }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={getCurrentLocation}
                            disabled={loading}
                            startIcon={<LocationOnIcon />}
                        >
                            Use Current Location
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography id="radius-slider" gutterBottom>
                            Radius: {newGeofence.radius} meters
                        </Typography>
                        <Slider
                            value={newGeofence.radius}
                            onChange={handleSliderChange}
                            aria-labelledby="radius-slider"
                            disabled={loading}
                            min={10}
                            max={1000}
                            step={10}
                            valueLabelDisplay="auto"
                            marks={[
                                { value: 10, label: '10m' },
                                { value: 500, label: '500m' },
                                { value: 1000, label: '1km' }
                            ]}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={addGeofence}
                            disabled={loading}
                            startIcon={<AddIcon />}
                        >
                            {loading ? <CircularProgress size={24} /> : "Add Geofence"}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
                Existing Geofences
            </Typography>

            {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}

            {!loading && geofences.length === 0 ? (
                <Alert severity="info">
                    No geofences have been set up yet. Add your first geofence above.
                </Alert>
            ) : (
                <List>
                    {geofences.map((geofence) => (
                        <ListItem key={geofence.id}>
                            <ListItemText
                                primary={geofence.name}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2">
                                            Coordinates: {geofence.latitude.toFixed(6)}, {geofence.longitude.toFixed(6)}
                                        </Typography>
                                        <br />
                                        <Typography component="span" variant="body2">
                                            Radius: {geofence.radius} meters
                                        </Typography>
                                    </>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() => deleteGeofence(geofence.id)}
                                    disabled={loading}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );
};

export default GeofenceManager;